/**
 * Auth service — orchestrates the auth API and session storage so pages
 * call a single layer. Mirrors process-runner's `signin.service.ts`
 * responsibilities: login, logout, refresh-token interval, post-login
 * setup, and the unauthorized-redirect helper used by the axios + Apollo
 * 401 handlers.
 */

import {
  clearAllSessionStorage,
  clearRefreshTokenLock,
  clearSigninJumperUrlPath,
  getRefreshTokenLock,
  getSigninJumperUrlPath,
  isValidSession,
  setAuthTokens,
  setLogoutUrl,
  setRefreshTokenLock,
  setSession,
  setSigninJumperUrlPath,
  setTenantDetails,
  setTenantIdHash,
  getSignedUser,
  setUserInfo,
} from '../session'
import {
  forgotPassword as apiForgotPassword,
  impersonate as apiImpersonate,
  getCurrentUserApi as apiGetCurrentUser,
  getTenantDetailsApi as apiGetTenantDetails,
  getLoginSettings as apiGetLoginSettings,
  login as apiLogin,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
  sendOtpCode as apiSendOtpCode,
  sysAdminAssume as apiSysAdminAssume,
  verifyOtp as apiVerifyOtp,
} from './auth.api'
import {
  AppType,
  type ForgotPasswordRequest,
  type LoginRequest,
  type LoginResponse,
  type LoginSettingResponse,
  LoginErrorEnum,
  type OtpVerifyResponse,
  type RefreshResponse,
} from './types'

// --------------------------------------------------------------------------
// Login completion — shared between password / OTP / SSO callback paths
// --------------------------------------------------------------------------

async function applyLoginResponse(
  res: LoginResponse,
  fallbackUsername?: string | null,
  fallbackTenantId?: string | null,
): Promise<void> {
  const responseTenantId = res.attributes?.tenantId ?? ''
  // The sysadmin-assume endpoint returns no top-level `username` — mirror
  // process-runner's `handleLoginResponse` and fall back to the
  // `sysadminusername` URL param (or any caller-provided fallback) so a
  // valid session is still written.
  const effectiveUsername = res.username || fallbackUsername || ''
  if (effectiveUsername && res.expiryTime) {
    setSession(effectiveUsername, res.expiryTime)
  }
  // Merge `res.user` with the top-level identity bits so ProfilePanel
  // and HomeHeader can render the actual signed-in user. process-runner
  // does this via `setUserDetails(res.data.user)` and a separate
  // `setSession(username, …)`; mobile stores a single blob.
  const top = res as LoginResponse & {
    firstName?: string
    lastName?: string
    role?: string
    email?: string
  }
  const userBlob: Record<string, unknown> = {
    ...(res.user as Record<string, unknown> | undefined),
    userName:
      res.username
      ?? (res.user as { userName?: string } | undefined)?.userName
      ?? fallbackUsername
      ?? undefined,
    email: top.email ?? (res.user as { email?: string } | undefined)?.email,
    firstName: top.firstName ?? (res.user as { firstName?: string } | undefined)?.firstName,
    lastName: top.lastName ?? (res.user as { lastName?: string } | undefined)?.lastName,
    role: top.role ?? (res.user as { role?: string } | undefined)?.role,
    picture: res.picture ?? (res.user as { picture?: string } | undefined)?.picture,
    appAdmin: res.appAdmin,
    buyerAdmin: res.buyerAdmin,
    anyAdmin: res.anyAdmin,
  }
  setUserInfo(userBlob)

  persistAuthTokens(res)

  const hashTenantId = responseTenantId || fallbackTenantId
  if (hashTenantId) setTenantIdHash(hashTenantId)

  // process-runner calls `loadUserDetails` → `GET /auth/users/me` before
  // `getTenantDetailsApi` so the buyer session cookie is established on
  // the same host that serves `/auth/tenants/{id}`.
  await loadCurrentUserAfterLogin()

  const tenant = await resolveLoginTenantDetails(
    res,
    responseTenantId,
    fallbackTenantId,
  )
  if (tenant) {
    setTenantDetails(tenant)
    setTenantIdHash(tenant.tenantId)
  }

  if (res.logoutUrl) setLogoutUrl(res.logoutUrl)
  // Lazy import to avoid circular dep
  void import('./broadcast').then(({ broadcastLogin }) => broadcastLogin())
}

function persistAuthTokens(res: LoginResponse | RefreshResponse): void {
  if (res.idToken || res.refreshToken) {
    setAuthTokens({ idToken: res.idToken, refreshToken: res.refreshToken })
  }
}

async function loadCurrentUserAfterLogin(): Promise<void> {
  try {
    const user = await apiGetCurrentUser({ skipAuthRedirect: true })
    if (user && typeof user === 'object') {
      setUserInfo({ ...getSignedUser(), ...user })
    }
  } catch {
    // Keep login-response user info when `/auth/users/me` is unavailable.
  }
}

async function resolveLoginTenantDetails(
  res: LoginResponse,
  responseTenantId: string,
  fallbackTenantId?: string | null,
) {
  const signedTenantId = getStringValue(getSignedUser(), 'tenantId')
  const userTenantId = getStringValue(res.user, 'tenantId')
  const lookupTenantId =
    signedTenantId || userTenantId || fallbackTenantId || responseTenantId || ''
  if (!lookupTenantId) return null

  try {
    return await apiGetTenantDetails(lookupTenantId, { skipAuthRedirect: true })
  } catch {
    return { tenantId: lookupTenantId }
  }
}

function getStringValue(source: unknown, key: string): string {
  if (!source || typeof source !== 'object') return ''
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : ''
}

// --------------------------------------------------------------------------
// Password login
// --------------------------------------------------------------------------

/**
 * Submit a `POST /auth/login`. Used for every flavour of password-side
 * sign-in:
 *   - Direct: `{ userName, password, tenantId? }`
 *   - Challenge response (SMS / new-password / MFA): the caller adds
 *     `respondingTo`, `sessionId`, and the challenge-specific field
 *     (`smsCode` or `password`).
 *
 * The session is hydrated only when the response has `username` +
 * `expiryTime` (i.e. a successful login). For challenge responses the
 * caller branches on `loginStatus` / `error.loginStatus` and routes to
 * the appropriate continuation screen.
 */
export async function passwordLogin(
  req: Omit<LoginRequest, 'appType'> & { appType?: typeof AppType.indirect },
): Promise<LoginResponse> {
  const body: LoginRequest = {
    appType: AppType.indirect,
    ...req,
  }
  // Drop empty `tenantId` so the backend's ambiguous-username detection
  // kicks in (and the form can prompt for one). Same behaviour as
  // process-runner.
  if (!body.tenantId || !body.tenantId.trim()) delete body.tenantId
  if (body.userName) body.userName = body.userName.trim()
  const res = await apiLogin(body)
  if (res.username && res.expiryTime) {
    await applyLoginResponse(res, null, body.tenantId)
  }
  return res
}

export async function getTenantLoginSettings(
  tenantId: string,
  targetUri?: string,
  userName?: string,
  connectionName?: string,
): Promise<LoginSettingResponse> {
  return apiGetLoginSettings(tenantId, targetUri, userName, connectionName)
}

// --------------------------------------------------------------------------
// SSO callback — code -> token exchange
// --------------------------------------------------------------------------

export async function ssoCodeLogin(
  code: string,
  clientId: string,
  redirectUri: string,
  tenantId: string,
  authType?: string,
): Promise<LoginResponse> {
  const body: LoginRequest = {
    code,
    clientId,
    uri: redirectUri,
    appType: AppType.indirect,
  }
  if (tenantId) body.tenantId = tenantId
  if (authType) body.authType = authType
  const res = await apiLogin(body)
  if (res.username && res.expiryTime) {
    await applyLoginResponse(res, null, tenantId)
  }
  return res
}

// --------------------------------------------------------------------------
// OTP flow
// --------------------------------------------------------------------------

export async function requestOtp(
  tenantId: string,
  convId: string,
  method: 'email' | 'mobile',
): Promise<string> {
  return apiSendOtpCode(tenantId, convId, method)
}

export async function confirmOtp(
  tenantId: string,
  convId: string,
  otp: string,
): Promise<OtpVerifyResponse> {
  const res = await apiVerifyOtp(tenantId, convId, otp)
  if (res.expiryTime && res.email) {
    setSession(res.email, res.expiryTime)
  }
  const t = res.attributes?.tenantId
  if (t) {
    const tenant = await resolveLoginTenantDetails(
      { attributes: { tenantId: t } },
      t,
      tenantId,
    )
    if (tenant) {
      setTenantDetails(tenant)
      setTenantIdHash(tenant.tenantId)
    }
  }
  return res
}

// --------------------------------------------------------------------------
// Impersonate / sysadmin assume — clear any prior session first, then
// hydrate from the impersonation response.
// --------------------------------------------------------------------------

export async function impersonateUser(
  tenantId: string,
  username: string,
  purpose: string,
): Promise<LoginResponse> {
  clearAllSessionStorage()
  const res = await apiImpersonate(tenantId, username, purpose)
  await applyLoginResponse(res, username, tenantId)
  return res
}

/**
 * `POST /auth/assume/{tid}[/{user}]?purpose=&appType=indirect` flow.
 * Mirrors process-runner's `handleSysAdminLogin`: clears any
 * lingering session, performs the assume call (via `fetch()` so the
 * axios session-check interceptor doesn't intercept), and on success
 * hydrates the new session via the shared `applyLoginResponse`.
 *
 * The caller is responsible for the post-success redirect (targetUrl /
 * jumperUrlPath resolution lives on the LoginPage) and for cleaning the
 * sysadmin URL params via `stripSysAdminParams()`.
 */
export async function sysAdminAssumeUser(
  sysAdminToken: string,
  tenantId: string,
  purpose: string,
  username?: string | null,
): Promise<LoginResponse> {
  // process-runner clears user + tenant + app urls before kicking off
  // the assume call so the previous user's data can't leak into
  // ProfilePanel / HomeHeader between request fire and response.
  clearAllSessionStorage()
  cancelRefreshToken()

  const res = await apiSysAdminAssume(sysAdminToken, tenantId, purpose, username)
  await applyLoginResponse(res, username, tenantId)
  // The assume URL is the source of truth for this flow. Persist it even
  // when the backend omits `attributes.tenantId`, so a second sysadmin
  // assume link can correctly detect tenant collisions.
  setTenantDetails({ tenantId })
  setTenantIdHash(tenantId)
  // If the backend returned a 2xx but without the bits we need to mint
  // a valid local session, surface it as an error so the caller doesn't
  // strip the URL params and bounce the user into a redirect loop
  // (post-login → AuthGuard → /login).
  if (!isValidSession()) {
    const err: LoginResponse = {
      error: {
        loginStatus: res.loginStatus ?? res.status,
        message: 'Sysadmin assume succeeded but no session was returned.',
      },
    }
    throw err
  }
  return res
}

/**
 * Remove the sysadmin-only query params from the current URL via
 * `history.replaceState` so the user doesn't see them after the assume
 * succeeds. Preserves `targetUrl` (it's a normal post-login concern, not
 * sysadmin-only), removes `sysadmintoken`, `sysadmintenantid`,
 * `sysadminusername`, `purpose`.
 */
export function stripSysAdminParams(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const sysAdminParamKeys = new Set([
    'sysadmintoken',
    'sysadmintenantid',
    'sysadminusername',
    'purpose',
  ])
  ;[...url.searchParams.keys()].forEach((key) => {
    if (sysAdminParamKeys.has(key.toLowerCase())) url.searchParams.delete(key)
  })
  window.history.replaceState(null, '', url.pathname + url.search + url.hash)
}

// --------------------------------------------------------------------------
// Forgot password
// --------------------------------------------------------------------------

export async function submitForgotPassword(
  req: ForgotPasswordRequest,
): Promise<string> {
  return apiForgotPassword(req)
}

// --------------------------------------------------------------------------
// Logout
// --------------------------------------------------------------------------

export async function logoutUser(): Promise<void> {
  try {
    if (isValidSession()) await apiLogout()
  } catch {
    /* swallow — clear local state regardless */
  } finally {
    clearSigninJumperUrlPath()
    clearAllSessionStorage()
    cancelRefreshToken()
    void import('./broadcast').then(({ broadcastLogout }) => broadcastLogout())
  }
}

// --------------------------------------------------------------------------
// Unauthorised redirect — used by axios + Apollo 401 handlers
// --------------------------------------------------------------------------

export function redirectAfterUnauthorizedAccess(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  // Capture where the user was so we can return them after login
  if (path !== '/login' && path !== '/logout' && path !== '/callback') {
    setSigninJumperUrlPath(`${path}${window.location.search}${window.location.hash}`)
  }
  clearAllSessionStorage()
  cancelRefreshToken()
  if (path !== '/login') {
    window.location.assign('/login')
  }
}

export function consumeSigninJumperUrl(): string {
  const target = getSigninJumperUrlPath()
  if (target) clearSigninJumperUrlPath()
  return target
}

/**
 * Post-login destination precedence — mirrors process-runner's
 * `redirectAfterLogin(jumperUrlPath)`:
 *   1. `signinJumperUrlPath` (set by AuthGuard / 401 interceptor)
 *   2. `targetUrl` query param (set when redirecting to /login)
 *   3. fallback (default `'/'`)
 *
 * Always returns a string the caller can pass to `navigate(target,
 * { replace: true })`. Clears `signinJumperUrlPath` as a side effect.
 */
export function resolvePostLoginTarget(
  targetUrlFromQuery?: string,
  fallback = '/',
): string {
  const jumper = consumeSigninJumperUrl()
  if (jumper) return jumper
  if (targetUrlFromQuery) {
    try {
      return decodeURIComponent(targetUrlFromQuery)
    } catch {
      return targetUrlFromQuery
    }
  }
  return fallback
}

// --------------------------------------------------------------------------
// Refresh-token interval (mirrors process-runner: 2.5–7.5 min jitter)
// --------------------------------------------------------------------------

let refreshTimer: ReturnType<typeof setTimeout> | null = null

export function cancelRefreshToken(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
  clearRefreshTokenLock()
}

function scheduleNextRefresh(
  expireAt: number,
  cb?: (err: boolean) => void,
): void {
  const now = Date.now()
  const marginMs = (2.5 + Math.random() * 5) * 60_000 // 2.5–7.5 min
  const fireAt = Math.max(now + 5_000, expireAt - marginMs)
  const delay = fireAt - now

  refreshTimer = setTimeout(async () => {
    if (getRefreshTokenLock()) {
      // Another tab is refreshing — re-check after a short delay
      refreshTimer = setTimeout(() => scheduleNextRefresh(expireAt, cb), 30_000)
      return
    }
    setRefreshTokenLock()
    try {
      const res = await apiRefreshToken()
      clearRefreshTokenLock()
      persistAuthTokens(res)
      if (res.expiryTime) {
        const username =
          res.username
          ?? getSignedUser().userName
          ?? getSignedUser().email
          ?? ''
        if (username) setSession(username, res.expiryTime)
        scheduleNextRefresh(res.expiryTime, cb)
        cb?.(false)
      } else {
        cb?.(true)
      }
    } catch (err: unknown) {
      clearRefreshTokenLock()
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 423 || (err as { code?: string })?.code === LoginErrorEnum.refreshTokenLocked) {
        // Another tab won; keep ourselves scheduled
        scheduleNextRefresh(expireAt, cb)
      } else {
        cb?.(true)
      }
    }
  }, delay)
}

export function commenceRefreshTokenInterval(
  cb?: (err: boolean) => void,
): void {
  cancelRefreshToken()
  const expireAt = Number(localStorage.getItem('expireAt') || 0)
  if (!expireAt || expireAt <= Date.now()) return
  scheduleNextRefresh(expireAt, cb)
}
